import { codeFrom, LOGIN_CODE_LENGTH } from "../login-code";

/**
 * Wires ui.tsx's `CodeField`: whatever is typed, pasted or autofilled becomes the code's digits
 * (`codeFrom`), the slots show them, the slot for the next digit is marked for app.css, and
 * `onComplete` runs once the last digit arrives. `render()` redraws after the script sets the
 * value itself.
 */
export function codeField(input: HTMLInputElement, onComplete: () => void) {
  const slots = [
    ...(input.parentElement?.querySelectorAll<HTMLElement>('[data-slot="code-slot"]') ?? []),
  ];

  function render() {
    const code = input.value;
    slots.forEach((slot, index) => {
      slot.textContent = code[index] ?? "";
      slot.toggleAttribute("data-active", index === Math.min(code.length, LOGIN_CODE_LENGTH - 1));
    });
  }

  // The caret always sits after the last digit, where the next one goes, as the slots show.
  const caretToEnd = () => input.setSelectionRange(input.value.length, input.value.length);

  function accept(value: string) {
    const code = codeFrom(value);
    input.value = code;
    input.removeAttribute("aria-invalid");
    caretToEnd();
    render();
    if (code.length === LOGIN_CODE_LENGTH) onComplete();
  }

  input.addEventListener("input", () => accept(input.value));
  // A whole pasted code replaces what was there, rather than landing after a digit already typed;
  // part of one goes where the selection is.
  input.addEventListener("paste", (event) => {
    const pasted = codeFrom(event.clipboardData?.getData("text") ?? "");
    if (!pasted) return;
    event.preventDefault();
    const { value, selectionStart: start, selectionEnd: end } = input;
    accept(
      pasted.length === LOGIN_CODE_LENGTH
        ? pasted
        : value.slice(0, start ?? value.length) + pasted + value.slice(end ?? value.length),
    );
  });
  for (const type of ["focus", "click"] as const) {
    input.addEventListener(type, () => {
      // After a failed code the script selects it all, so the next digit replaces it.
      if (input.selectionStart === 0 && input.selectionEnd === input.value.length) return;
      caretToEnd();
    });
  }
  render();
  return { render };
}
